import React, { useEffect, useMemo, useRef, useState } from "react"

import { Icon } from "components/Icon";
import { Alert } from "services/Alert";
import Recaptcha from "react-google-invisible-recaptcha";
import { Link, withRouter } from "react-router-dom";
import styled, { css } from "styled-components";
import { connect } from "react-redux";
import _ from "lodash";

import { loginUser, loginBySessionKey, clearSession, changeLanguage } from "actions/auth";
import { clearLocalStorage, isError, hideRecaptchaOverlay } from "utils";
import { LOCAL_STORAGE_USER_INFO } from "const/localStorageKeys";
import { APP_TYPE_ADMIN, APP_TYPE_DEFAULT } from "const";
import { withTranslation } from "hoc/WithTranslation";
import { BaseButton, ButtonToolbar } from "components/base/BaseButton";
import LangSwitcher from "components/LangSwitcher";
import { Spacer } from "components/base/Spacer";
import AccountSwitchingInfo from "./AccountSwitchingInfo";
import RegistrationModal from "./RegistrationModal";
import { RtlContext } from "../../App";
import m from "definedMessages";

import { Badge, Button, Container, Content, Form, Header, Message, Nav, Navbar, Panel, Schema } from "rsuite";

const { StringType } = Schema.Types;
const SHOW_LOGIN_MESSAGE = false;

export const loginModel = Schema.Model({
    login: StringType().isRequired("Required"),
    password: StringType().isRequired("Required"),
});

export const secondAuthModel = Schema.Model({
    second_password: StringType()
        .isRequired("Required")
        .maxLength(8, "The maximum of this field is 8 characters")
});

const renderMessageWithLineBreaks = (content) => React.Children.toArray(content).flatMap((node, nodeIndex) => {
    if (typeof node !== "string") {
        return node;
    }

    return node.split("\n").flatMap((part, partIndex) => {
        const result = [];

        if (partIndex > 0) {
            result.push(<br key={`break-${nodeIndex}-${partIndex}`} />);
        }

        if (part) {
            result.push(
                <React.Fragment key={`text-${nodeIndex}-${partIndex}`}>
                    {part}
                </React.Fragment>
            );
        }

        return result;
    });
});


const Login = (
    {
        loginUser,
        loginBySessionKey,
        changeLanguage,

        loading,
        captchaNeeded,
        secondAuthType,
        secondAuthLink,
        secondAuthEmail,
        location,
        history,
        lang,
        rtl,

        formatMessage
    }
) => {

    const formData = useRef(null);

    const recaptchaRef = useRef(null);
    const enableCaptcha = process.env.REACT_APP_ENABLE_CAPTCHA === "1";
    const recapchaIsVisible = enableCaptcha && captchaNeeded && !secondAuthType;
    const storedUserInfo = useMemo(() => {
        return localStorage.getItem(LOCAL_STORAGE_USER_INFO)
            ? JSON.parse(localStorage.getItem(LOCAL_STORAGE_USER_INFO))
            : {};
    }, []);

    const { account_name, name, login } = storedUserInfo;
    const defaultValue = useMemo(() => {
        return APP_TYPE_DEFAULT === APP_TYPE_ADMIN
            ? ({ login })
            : ({ login, lang });
    }, [login, lang]);

    const [showPassword, onChangeShowPassword] = useState(true);
    const [showRegistration, onShowRegistrationModal] = useState(new URLSearchParams(location.search).has("sign-up"));
    const [emailPending, setEmailPending] = useState(null);
    const [loginFormValue, setLoginFormValue] = useState(defaultValue);
    const [secondAuthFormValue, setSecondAuthFormValue] = useState({ email: secondAuthEmail });
    const [sessionKey, setSesionKey] = useState(() => {
        return new URLSearchParams(location.search).get("session-key") || null;
    });
    const [language, setLanguage] = useState(lang);
    const languageRef = useRef(language);
    const [hasHiddenRecaptcha, setHiddenRecaptcha] = useState(false);

    useEffect(() => {
        if (!sessionKey) {
            return;
        }

        history.replace("/reload");

        loginBySessionKey(sessionKey);
    }, [sessionKey]);

    useEffect(() => {
        languageRef.current = language;
    }, [language]);

    useEffect(() => {
        setSecondAuthFormValue({ email: secondAuthEmail });
    }, [secondAuthEmail]);

    // Uncomment in case of need to make Login page RTL
    // useEffect(() => {
    //     if (APP_TYPE_DEFAULT !== APP_TYPE_CLIENT) {
    //         return;
    //     }

    //     if (rtl) {
    //         document.body.classList.add("rtl");
    //     } else {
    //         document.body.classList.remove("rtl");
    //     }
    // }, [rtl, lang]);

    useEffect(() => {
        if (!recapchaIsVisible) {
            resetRecaptcha();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [recapchaIsVisible, secondAuthType]);

    useEffect(() => {
        return () => resetRecaptcha();
    });


    const submit = (formValue) => {
        if (loading) {
            return;
        }

        formData.current = formValue;

        executeRecaptcha();
    };

    const secondFactorSubmit = (formValue) => {
        if (loading) {
            return;
        }

        formData.current = formValue;

        executeRecaptcha();
    };

    const executeRecaptcha = () => {
        if (!recapchaIsVisible) {
            handleResolveCaptcha();
            return;
        }

        if (!recaptchaRef?.current?.execute) {
            handleResolveCaptcha();
            return;
        }

        recaptchaRef.current.execute();
    };

    const resetRecaptcha = () => {
        if (!recapchaIsVisible) {
            return;
        }

        if (!recaptchaRef?.current?.reset) {
            return;
        }

        recaptchaRef.current.reset();
    };

    const handleResolveCaptcha = (token) => {
        if (!token && recapchaIsVisible) {
            Alert.error("Bot verification failed");
            resetRecaptcha();
            return;
        }

        loginUser({
            ...formData.current, lang: languageRef.current,
            captcha_token: token, loginLocation: location, loginHistory: history
        })
            .then((r) => {
                if (isError(r)) {
                    const reasonCode = r.response?.data.error?.reason_code || null;

                    if ("not_confirmed" === reasonCode) {
                        setEmailPending(r.response?.data?.error?.data?.email || null);
                    }
                }

                clearLocalStorage();
            })
            .finally(() => {
                // resetRecaptcha();

                const observer = new MutationObserver((mutations) => {
                    for (const mutation of mutations) {
                        for (const node of mutation.addedNodes) {
                            if (
                                node.tagName === "IFRAME" &&
                                node.style &&
                                node.style.zIndex === "2147483647"
                            ) {
                                hideRecaptchaOverlay(hasHiddenRecaptcha, setHiddenRecaptcha);
                                observer.disconnect();
                                return;
                            }
                        }
                    }
                });

                observer.observe(document.body, {
                    childList: true,
                    subtree: true,
                });
            });
    };

    if (sessionKey) {
        return (
            <AccountSwitchingInfo />
        );
    }

    const LOGIN_MESSAGE_LINK = "https://api.iprn-elite.com";
    const LOGIN_MESSAGE = formatMessage(m.loginWarning, {
        link: <NoBreakLink href={LOGIN_MESSAGE_LINK} target="_blank" rel="noreferrer" title={LOGIN_MESSAGE_LINK}>{LOGIN_MESSAGE_LINK}</NoBreakLink>
    });
    const LOGIN_MESSAGE_DESCRIPTION = renderMessageWithLineBreaks(LOGIN_MESSAGE);

    return (
        <RtlContext.Provider value={false}>
            <StyledContainer>
                <Header>
                    <Navbar appearance="inverse">
                        <Navbar.Header>
                            {account_name && name &&
                                <StyledMenuUserInfo>
                                    <StyledMenuCompanyInfo>
                                        <StyledBadge />
                                        {account_name}
                                    </StyledMenuCompanyInfo>
                                    {name}
                                </StyledMenuUserInfo>
                            }
                        </Navbar.Header>

                        <Navbar.Body>
                            <Nav pullRight>
                                <StyledNavItem
                                    icon={<Icon icon="sign-in" />}
                                >
                                    Log in
                                </StyledNavItem>
                            </Nav>
                        </Navbar.Body>

                    </Navbar>
                </Header>

                <Content>
                    {APP_TYPE_DEFAULT !== APP_TYPE_ADMIN && (
                        SHOW_LOGIN_MESSAGE
                            ? <LoginMessage
                                showIcon
                                rtl={rtl}
                                type="warning"
                            >
                                {LOGIN_MESSAGE_DESCRIPTION}
                            </LoginMessage>
                            : <LoginMessagePlaceholder aria-hidden />
                    )}
                    <StyledPanel
                        header={<h3>Login</h3>}
                        topMargin={APP_TYPE_DEFAULT === APP_TYPE_ADMIN}
                        // style={{marginTop: "15vh"}}
                        bordered
                    >

                        {!secondAuthType &&
                            <Form
                                fluid
                                formValue={loginFormValue}
                                onChange={setLoginFormValue}
                                onSubmit={submit}
                                model={loginModel}
                            >
                                <Form.Group>
                                    <Form.ControlLabel>Email address or login</Form.ControlLabel>
                                    <Form.Control autoFocus={!login} name="login" />
                                </Form.Group>

                                <Form.Group>
                                    <Form.ControlLabel>Password</Form.ControlLabel>
                                    <Form.Control
                                        autoFocus={!!login}
                                        name="password"
                                        type={showPassword ? "password" : "text"}
                                    />
                                    <Button
                                        onClick={() => onChangeShowPassword(!showPassword)}
                                        style={{
                                            position: "absolute",
                                            marginLeft: "-38px",
                                            background: "transparent",
                                            ...(rtl ? {
                                                padding: "8px 4px"
                                            } : {})
                                        }}>
                                        <Icon icon={showPassword ? "eye" : "eye-slash"} />
                                    </Button>
                                </Form.Group>

                                {APP_TYPE_DEFAULT !== APP_TYPE_ADMIN &&
                                    <Form.Group>
                                        <Form.ControlLabel>Language</Form.ControlLabel>
                                        <Form.Control
                                            accepter={LangSwitcher}
                                            onChange={(lang) => {
                                                changeLanguage(lang, lang === "ar");
                                                setLanguage(lang)
                                            }}
                                            name="lang"
                                            menuClassName="no-rtl"
                                        />
                                    </Form.Group>
                                }

                                <Form.Group>
                                    <ButtonToolbar>
                                        <StyledButton
                                            type="submit"
                                            disabled={loading}
                                        >
                                            Sign in
                                        </StyledButton>


                                        <Link to="/reset-password">Forgot password?</Link>
                                    </ButtonToolbar>
                                    <ButtonToolbar>
                                        {APP_TYPE_DEFAULT !== APP_TYPE_ADMIN &&
                                            <StyledSignUpButton
                                                appearance="link"
                                                onClick={() => {
                                                    onShowRegistrationModal(true)
                                                }}
                                                disabled={loading}
                                            >
                                                Looking to sign up? Contact us!
                                            </StyledSignUpButton>
                                        }
                                    </ButtonToolbar>
                                </Form.Group>
                            </Form>
                        }

                        {secondAuthType &&
                            <Form
                                formValue={secondAuthFormValue}
                                onChange={setSecondAuthFormValue}
                                onSubmit={secondFactorSubmit}
                                model={secondAuthModel}
                                style={{ display: "block" }}
                            >
                                <div role="alert" className="alert alert-warning">
                                    Your account is under two factor authentication, please enter code which we sent to
                                    your {secondAuthType} <b>{secondAuthLink}</b>
                                </div>

                                <Form.Control
                                    name="email"
                                    hidden={true}
                                />

                                <Form.Control
                                    name="second_password"
                                    placeholder="Enter your code here"
                                    autoFocus={true}
                                    style={{
                                        display: "block",
                                        width: "100%",
                                        marginBottom: "20px"
                                    }}
                                />

                                <Form.Group>
                                    <ButtonToolbar>
                                        <StyledButton
                                            type="submit"
                                            disabled={loading}
                                        >
                                            Confirm
                                        </StyledButton>
                                    </ButtonToolbar>
                                </Form.Group>
                            </Form>
                        }

                        <Spacer />

                        {emailPending &&
                            <>
                                <Message
                                    showIcon
                                    closable
                                    type="warning"
                                    onClose={() => setEmailPending(null)}
                                >
                                    Email is not verified. Please check your address <b>{emailPending}</b> for details
                                </Message>
                            </>
                        }

                    </StyledPanel>
                </Content>
                {/* 
                    <ManualWidget/>
                    <VersionLabel/> 
                */}

                <RegistrationModal
                    maxWidth={700}
                    show={showRegistration}
                    captchaNeeded={captchaNeeded}
                    onClose={onShowRegistrationModal}
                />


                {recapchaIsVisible &&
                    <Recaptcha
                        ref={recaptchaRef}
                        sitekey={process.env.REACT_APP_SITE_KEY}
                        onResolved={handleResolveCaptcha}
                    />
                }

            </StyledContainer>
        </RtlContext.Provider>
    );
};

const mapState = ({ auth }) => ({
    rtl: auth.rtl,
    loading: auth.loading,
    secondAuthType: auth.secondAuthType,
    secondAuthLink: auth.secondAuthLink,
    secondAuthEmail: auth.secondAuthEmail,
    captchaNeeded: auth.captchaNeeded,
    lang: auth.lang,
});

export default connect(mapState, {
    loginUser,
    loginBySessionKey,

    changeLanguage
})(withRouter(withTranslation(Login)));

const StyledSignUpButton = styled(Button)`
    font-size: 13px !important;
    margin-top: 18px;
    padding: 0 !important;
`;

const StyledContainer = styled(Container)`
    min-height: 100vh;
`;

const StyledPanel = styled(Panel)`
    margin-top: ${props => props.topMargin ? "15" : "0"}vh;
    margin-left: auto;
    margin-right: auto;
    max-width: 500px;
    min-width: 340px;
    width: 100%;
`;

const StyledMenuCompanyInfo = styled.div`
    position: absolute;
    left: 4px;
    top: 4px;
    font-size: 8px;
`;

const StyledMenuUserInfo = styled.li`
    display: inline-block;
    padding: 19px 16px;
    height: 56px;
    font-size: 13px;
    color: #fff;
`;

const StyledBadge = styled(Badge)`
    &&& {
        margin-right: 2px;
        width: 4px;
        height: 4px;
        position: relative;
        top: -4px;
    }
`;

const StyledNavItem = styled(Nav.Item)`
    .rs-nav-item-content {
    
        &:hover,
        &:active,
        &:focus {
            background-color: #5f2bb3 !important;
            
            .app-client & {
                background-color: #20b188 !important;
            }
        }
    }
`;

const StyledButton = styled(BaseButton).attrs(() => {
    return {
        buttonStyle: APP_TYPE_DEFAULT === APP_TYPE_ADMIN ? "secondary" : "primary"
    }
})`
    && {
        min-width: 0;
    }
`;

const LoginMessage = styled(Message)`
    && {
        display: block;
        margin: 10vh auto 15px auto;
        max-width: 500px;
        min-width: 340px;
        width: 100%;
    }

    .rs-message-body {
        white-space: pre-line;
    }

    ${props => props.rtl && css`
        .rs-message-container {
            padding-left: 20px !important;
        }
        .rs-message-icon-wrapper {
            float: right;
            position: relative !important;
            top: 0px !important;
            left: 0px !important;
        }
        .rs-message-body {
            direction: rtl;
            text-align: right;
        }
        .rs-message-content {
            padding-right: 20px;
        }
    `}
`;

const LoginMessagePlaceholder = styled.div`
    margin: 10vh auto 15px auto;
    max-width: 500px;
    min-width: 340px;
    width: 100%;
    min-height: 52px;
`;

const NoBreakLink = styled.a`
    display: inline-block;
    white-space: nowrap;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    vertical-align: bottom;
`;
